import api from "../services/authService";

/**
 * Per-Matter Processing page client.
 *
 * Consumes the backend review-gate surface (PRs #683/#685/...):
 *   GET /api/pipeline/matters/<matter_id>/processing/
 * which returns one row per document with a plain Processed/Processing/Errored
 * status, per-stage checks (extracted/categorized/graphed/embedded), category,
 * page + text sizes, a human-readable failure reason, and any review decision.
 *
 * All calls route through the shared `authService` axios instance so JWT + 401
 * refresh stay consistent with the rest of the app. `authService` already
 * prefixes the configured API base URL, so paths here start at `/pipeline`.
 */

// The BE clamps `limit` to 500 per request (default 100). Big matters page.
const PAGE_LIMIT = 500;

// Hard stop for the page fetch. Axios has NO default timeout, so when the
// backend hung on live Temporal status queries (Armijo 2026-07-27: nginx cut
// the response at 600s), the promise never settled, the page's in-flight
// guard never cleared, and Retry/Refresh became permanent no-ops. The backend
// now budgets its live enrichment to seconds; 45s is generous headroom.
export const PROCESSING_PAGE_TIMEOUT_MS = 45_000;

export const getMatterProcessingPage = async (
  matterId,
  { offset = 0, limit = PAGE_LIMIT } = {}
) => {
  const usp = new URLSearchParams();
  if (offset) usp.set("offset", String(offset));
  if (limit) usp.set("limit", String(limit));
  const qs = usp.toString();
  const { data } = await api.get(
    `/pipeline/matters/${matterId}/processing/${qs ? `?${qs}` : ""}`,
    { timeout: PROCESSING_PAGE_TIMEOUT_MS }
  );
  return data;
};

/**
 * Fetch EVERY document for the matter (the page shows all of them). The BE
 * clamps `limit` to 500, so we page through by offset until we've collected
 * `total_documents` rows. The `summary` counts are matter-wide on every
 * response, so the first page's summary is authoritative — we keep it and only
 * accumulate `documents`.
 */
export const getAllMatterProcessingRows = async (matterId) => {
  const first = await getMatterProcessingPage(matterId, { offset: 0, limit: PAGE_LIMIT });
  const total = Number(first.total_documents || 0);
  let documents = [...(first.documents || [])];
  let offset = documents.length;
  // Guard against an unexpected server response that would otherwise loop
  // forever: stop as soon as a page returns nothing.
  while (documents.length < total) {
    const next = await getMatterProcessingPage(matterId, { offset, limit: PAGE_LIMIT });
    const rows = next.documents || [];
    if (rows.length === 0) break;
    documents = documents.concat(rows);
    offset += rows.length;
  }
  return { ...first, documents };
};

/**
 * Change a document's category from the Processing page.
 *
 * Reuses the category-correction endpoint but with the training side-effects
 * OFF: a quick inline fix on this page should NOT silently mint
 * negative-example / gold-standard training rows — that richer "report
 * incorrect categorization" flow lives in the dedicated AI Categorization UI.
 *
 * NOTE: the endpoint is admin-only on the backend (IsRoleAdmin). The page only
 * renders the editable dropdown for admins; non-admins see the category as
 * plain text.
 */
export const changeDocumentCategory = async (documentId, correctCategory) => {
  const { data } = await api.post(
    `/pipeline/categorization/correct/${documentId}/`,
    {
      correct_category: correctCategory,
      add_negative: false,
      add_gold_standard: false,
    }
  );
  return data;
};

/**
 * Re-run a single errored document through the pipeline. Errors (e.g. the
 * document isn't in a retryable state) come back as 4xx with `{ error }`.
 */
export const retryDocument = async (matterId, documentId) => {
  const { data } = await api.post(
    `/pipeline/matters/${matterId}/documents/${documentId}/retry/`
  );
  return data;
};

/**
 * Mark a document "not needed" — it stops blocking the matter from completing.
 * Optional `reason` is stored for the audit trail. Returns the updated review
 * fields `{ document_id, review_decision, review_reason }`.
 */
export const markDocumentNotNeeded = async (matterId, documentId, reason = "") => {
  const { data } = await api.post(
    `/pipeline/matters/${matterId}/documents/${documentId}/mark-not-needed/`,
    { reason }
  );
  return data;
};

/**
 * Mark a document "required" — it MUST process successfully before the matter
 * can complete. Optional `reason` is stored for the audit trail.
 */
export const markDocumentRequired = async (matterId, documentId, reason = "") => {
  const { data } = await api.post(
    `/pipeline/matters/${matterId}/documents/${documentId}/mark-required/`,
    { reason }
  );
  return data;
};

/** Clear a prior not-needed / required decision (back to "none"). */
export const clearDocumentReview = async (matterId, documentId) => {
  const { data } = await api.post(
    `/pipeline/matters/${matterId}/documents/${documentId}/clear-review/`
  );
  return data;
};

/**
 * Whether the matter's failures are resolved enough to complete. Drives the
 * "N documents need your decision" banner. Returns
 * `{ resolved, needs_review_count, in_flight_count, ... }`.
 */
export const getMatterReviewResolution = async (matterId) => {
  const { data } = await api.get(
    `/pipeline/matters/${matterId}/review-resolution/`,
    { timeout: PROCESSING_PAGE_TIMEOUT_MS }
  );
  return data;
};
